import { useEffect, useRef } from "react";
import * as THREE from "three";

const VERT = `
uniform float uTravel;
uniform float uDepth;
uniform float uStretch;
uniform float uTime;
uniform vec2 uMouse;
uniform float uAspect;
uniform float uPush;
attribute vec3 aSeed;
attribute float aSide;
attribute float aPhase;
varying float vAlpha;
varying float vGlow;
void main() {
  float z = mod(aSeed.z + uTravel, uDepth);
  float headZ = z - uDepth;
  vec3 pos = vec3(aSeed.xy, headZ - aSide * uStretch);
  vec4 mv = modelViewMatrix * vec4(pos, 1.0);

  vec4 clip = projectionMatrix * mv;
  vec2 ndc = clip.xy / clip.w;
  vec2 diff = (ndc - uMouse) * vec2(uAspect, 1.0);
  float d = length(diff);
  float force = smoothstep(0.6, 0.0, d);
  vec2 dir = d > 0.0001 ? normalize(diff) : vec2(0.0, 1.0);
  mv.xy += dir * force * uPush * (-mv.z) * 0.0022;
  vGlow = force;

  float tw = 0.7 + 0.3 * sin(uTime * 2.0 + aPhase * 50.0);
  float nearFade = smoothstep(0.0, -70.0, headZ);
  float farFade = smoothstep(-uDepth, -uDepth + 260.0, headZ);
  vAlpha = (1.0 - aSide * 0.92) * nearFade * farFade * tw;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = `
uniform vec3 uColor;
uniform float uOpacity;
varying float vAlpha;
varying float vGlow;
void main() {
  float a = vAlpha * uOpacity * (1.0 + vGlow * 1.6);
  if (a < 0.004) discard;
  vec3 col = mix(uColor, vec3(1.0), vGlow * 0.5);
  gl_FragColor = vec4(col, a);
}
`;

// 3 parallax layers: far white, mid gold, near bright warm gold
const LAYERS = [
  { count: 2200, color: 0xffffff, opacity: 0.55, speed: 40, stretch: 0.7, push: 0.5, rMin: 60, rMax: 520 },
  { count: 1600, color: 0xffd700, opacity: 0.8, speed: 78, stretch: 1.0, push: 1.0, rMin: 45, rMax: 460 },
  { count: 900, color: 0xffe9a8, opacity: 0.95, speed: 125, stretch: 1.45, push: 1.6, rMin: 35, rMax: 400 },
];

const DEPTH = 1000;

export const Starfield = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    const isMobile = window.innerWidth < 768;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(dpr);
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      mount.clientWidth / mount.clientHeight,
      0.1,
      1600
    );
    camera.position.set(0, 0, 0);

    const group = new THREE.Group();
    scene.add(group);

    const layers = LAYERS.map((cfg) => {
      const count = Math.floor(cfg.count * (isMobile ? 0.4 : 1));
      const verts = count * 2;
      const seed = new Float32Array(verts * 3);
      const side = new Float32Array(verts);
      const phase = new Float32Array(verts);
      const posDummy = new Float32Array(verts * 3);
      for (let i = 0; i < count; i++) {
        const a = Math.random() * Math.PI * 2;
        const r = Math.sqrt(
          cfg.rMin * cfg.rMin +
            Math.random() * (cfg.rMax * cfg.rMax - cfg.rMin * cfg.rMin)
        );
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r * 0.72;
        const z = Math.random() * DEPTH;
        const ph = Math.random();
        for (let v = 0; v < 2; v++) {
          const idx = i * 2 + v;
          seed[idx * 3] = x;
          seed[idx * 3 + 1] = y;
          seed[idx * 3 + 2] = z;
          side[idx] = v;
          phase[idx] = ph;
        }
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(posDummy, 3));
      geo.setAttribute("aSeed", new THREE.BufferAttribute(seed, 3));
      geo.setAttribute("aSide", new THREE.BufferAttribute(side, 1));
      geo.setAttribute("aPhase", new THREE.BufferAttribute(phase, 1));
      const mat = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        uniforms: {
          uTime: { value: 0 },
          uTravel: { value: Math.random() * DEPTH },
          uDepth: { value: DEPTH },
          uStretch: { value: 20 },
          uColor: { value: new THREE.Color(cfg.color) },
          uOpacity: { value: 0 },
          uMouse: { value: new THREE.Vector2(10, 10) },
          uAspect: { value: mount.clientWidth / mount.clientHeight },
          uPush: { value: isMobile ? 0 : cfg.push },
        },
        vertexShader: VERT,
        fragmentShader: FRAG,
      });
      const lines = new THREE.LineSegments(geo, mat);
      lines.frustumCulled = false;
      group.add(lines);
      return { lines, mat, cfg };
    });

    const mouseNdc = new THREE.Vector2(10, 10);
    const mouseTarget = new THREE.Vector2(10, 10);
    const pointer = { x: 0, y: 0 };
    const onPointer = (e) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
      const r = mount.getBoundingClientRect();
      mouseTarget.set(
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -(((e.clientY - r.top) / r.height) * 2 - 1)
      );
    };
    if (!isMobile) window.addEventListener("pointermove", onPointer);

    let visible = true;
    const io = new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
    });
    io.observe(mount);

    let lastScroll = window.scrollY;
    let warp = 0;
    const clock = new THREE.Clock();
    const start = performance.now();
    let raf;

    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (!visible || document.hidden) return;
      const dt = Math.min(clock.getDelta(), 0.05);
      const t = clock.elapsedTime;

      // hyperspace intro: 10x speed, easing out over 3.4s
      const introT = Math.min((performance.now() - start) / 3400, 1);
      const introEase = 1 - Math.pow(1 - introT, 4);
      const speedFactor = 1 + (1 - introEase) * 9;

      const sy = window.scrollY;
      const dy = Math.abs(sy - lastScroll);
      lastScroll = sy;
      warp += (Math.min(dy * 0.02, 4) - warp) * Math.min(dt * 6, 1);

      mouseNdc.lerp(mouseTarget, Math.min(dt * 8, 1));

      layers.forEach((l) => {
        const speed = l.cfg.speed * (speedFactor + warp);
        l.mat.uniforms.uTravel.value += speed * dt;
        l.mat.uniforms.uStretch.value = THREE.MathUtils.clamp(
          speed * 0.5 * l.cfg.stretch,
          14 * l.cfg.stretch,
          300
        );
        l.mat.uniforms.uTime.value = t;
        l.mat.uniforms.uOpacity.value = l.cfg.opacity * Math.min(introT * 2.5, 1);
        l.mat.uniforms.uMouse.value.copy(mouseNdc);
      });

      group.rotation.y += (pointer.x * 0.1 - group.rotation.y) * 0.05;
      group.rotation.x += (-pointer.y * 0.07 - group.rotation.x) * 0.05;

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      layers.forEach((l) => (l.mat.uniforms.uAspect.value = w / h));
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("pointermove", onPointer);
      window.removeEventListener("resize", onResize);
      layers.forEach((l) => {
        l.lines.geometry.dispose();
        l.mat.dispose();
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount)
        mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="absolute inset-0 z-0"
      data-testid="hero-starfield"
    />
  );
};
